import SectionTitle from "@/components/ui/section-title";
import useQueryOrganization from "@/hooks/queries/useQueryOrganization";
import { Params } from "@/types/utils";
import { i18n } from "@/utils/i18n";
import { Button } from "@aidergroup/ui-v2";
import {
    Autocomplete,
    Box,
    Chip,
    FormControlLabel,
    Skeleton,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import React from "react";
import { useParams } from "react-router-dom";
import { Styled } from "./styles";
import { RECIPIENTS_MAX, useNotificationSettings } from "./hooks";

const Notifications: React.FC = () => {
    const { oid } = useParams<Params>();
    const { data: organization, isLoading } = useQueryOrganization();

    // The query follows the organization in client storage, which may differ
    // from the URL. Settings for any other organization, or none at all after
    // a failed load, must never be shown as editable defaults.
    const current = organization?.id === oid ? organization : undefined;

    const {
        enabled,
        recipients,
        error,
        saving,
        isDirty,
        onChangeEnabled,
        onChangeRecipients,
        onSave,
    } = useNotificationSettings(oid, current?.emailNotifications);

    return (
        <Box padding="0px 40px 40px">
            <SectionTitle title={i18n.t("notifications.sectionTitle")} />
            <Styled.Panel>
                <Typography variant="body2" color="text.secondary">
                    {i18n.t("notifications.helper")}
                </Typography>

                {isLoading || !current ? (
                    <Skeleton variant="rounded" width={480} height={120} />
                ) : (
                    <React.Fragment>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={enabled}
                                    onChange={(_, checked) => onChangeEnabled(checked)}
                                />
                            }
                            label={i18n.t("notifications.toggleLabel")}
                        />

                        {/* Kept out of Sentry session replays: recipients may be named people. */}
                        <Box data-sentry-block width="100%">
                            <Autocomplete
                                multiple
                                freeSolo
                                fullWidth
                                options={[] as string[]}
                                value={recipients}
                                onChange={(_, value) => onChangeRecipients(value)}
                                renderTags={(value, getTagProps) =>
                                    value.map((recipient, index) => {
                                        const { key, ...tagProps } = getTagProps({ index });

                                        return (
                                            <Chip
                                                key={key}
                                                variant="outlined"
                                                label={recipient}
                                                {...tagProps}
                                            />
                                        );
                                    })
                                }
                                renderInput={params => (
                                    <TextField
                                        {...params}
                                        label={i18n.t("notifications.recipientsLabel")}
                                        placeholder="namn@kommun.se"
                                        error={Boolean(error)}
                                        helperText={
                                            error || i18n.t("notifications.recipientsHelper")
                                        }
                                    />
                                )}
                            />
                        </Box>

                        <Typography variant="caption" color="text.secondary">
                            {i18n.t("notifications.frequencyNote")}
                        </Typography>

                        <Button
                            variant="contained"
                            onClick={onSave}
                            disabled={!isDirty || saving || recipients.length > RECIPIENTS_MAX}
                            loading={saving}
                        >
                            {i18n.t("button.saveNotifications")}
                        </Button>
                    </React.Fragment>
                )}
            </Styled.Panel>
        </Box>
    );
};

export default Notifications;
